import { useState } from 'react';
import {
    ArchiveIcon,
    ArchiveRestoreIcon,
    BellIcon,
    BellOffIcon,
    EllipsisVerticalIcon,
} from 'lucide-react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import type { Conversation } from '../../types';
import { useUpdateConversation } from '../../hooks/use-update-conversation';

/**
 * The more options button for a chat box. Shows a popover with more options.
 * @see {@link ChatBox}
 */
export function ChatBoxMoreOptions({
    conversation,
}: {
    conversation: Conversation;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const { archiveConversation, muteConversation } = useUpdateConversation({
        conversationID: Number(conversation.ID),
    });

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <button
                    className="flex size-[36px] items-center justify-center rounded-full hover:bg-black/10"
                    aria-label="More options"
                    onClick={(e) => e.stopPropagation()}
                >
                    <EllipsisVerticalIcon className="size-[16px]" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[180px] overflow-hidden rounded-[8px] bg-[#DADADA] p-0"
                variant="shadcn"
                background="none"
                align="end"
            >
                <ul className="flex flex-col text-[12px] leading-tight">
                    <li
                        className="flex items-center gap-[8px] p-[10px] hover:bg-black/5"
                        role="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            muteConversation(!conversation.IsMuted);
                            setIsOpen(false);
                        }}
                    >
                        {conversation.IsMuted ? (
                            <BellIcon className="size-[14px]" />
                        ) : (
                            <BellOffIcon className="size-[14px]" />
                        )}
                        <span>
                            {conversation.IsMuted
                                ? 'Unmute conversation'
                                : 'Mute conversation'}
                        </span>
                    </li>
                    <li
                        className="flex items-center gap-[8px] p-[10px] hover:bg-black/5"
                        role="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            archiveConversation(!conversation.IsArchived);
                            setIsOpen(false);
                        }}
                    >
                        {conversation.IsArchived ? (
                            <ArchiveRestoreIcon className="size-[14px]" />
                        ) : (
                            <ArchiveIcon className="size-[14px]" />
                        )}
                        <span>
                            {conversation.IsArchived
                                ? 'Unarchive conversation'
                                : 'Archive conversation'}
                        </span>
                    </li>
                </ul>
            </PopoverContent>
        </Popover>
    );
}
